import {
  TextInput,
  PasswordInput,
  Checkbox,
  Anchor,
  Paper,
  Container,
  Modal,
} from "@mantine/core";
import { message } from "antd";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AppVersion, PageTitle } from "../../components";
import { postLogin } from "../../helpers/api";
import { authenticate, setRole } from "../../helpers/auth";
import { appLinks, termsandConditions } from "../../helpers/config";
import riviaOS from "../../static/img/logo.jpg";

export function LoginPage() {
  const [login, setLogin] = useState({
    email: "",
    password: "",
    tnc: false,
  });
  const [busy, setBusy] = useState(false);
  const [modelOpen, setOpenModal] = useState(false);
  const [returnUrl, setReturnUrl] = useState(null);

  const nav = useNavigate();
  const [searchParams] = useSearchParams();

  // Check for returnUrl parameter on component mount
  useEffect(() => {
    const urlParam = searchParams.get("returnUrl");
    if (urlParam) {
      // Verify if the returnUrl is an authorized URL
      const authorizedUrl = process.env.REACT_APP_BOS_URL;

      if (authorizedUrl && urlParam.startsWith(authorizedUrl)) {
        setReturnUrl(urlParam);
      } else {
        setReturnUrl(null);
        message.error("Invalid return URL");
      }
    }
  }, [searchParams]);

  const handleLogin = (e) => {
    e.preventDefault();

    if (!login.password || !login.email) {
      message.info("all fields are required");
      return;
    }

    if (!login.tnc) {
      message.error("You must accept the terms and conditions");
      return;
    }

    setBusy(true);
    postLogin(login)
      .then((res) => {
        if (res.status === 200) {
          authenticate(res.data.token, res.data.data);
          setRole(res.data.data.role);

          // If returnUrl is provided and authorized, navigate to it with auth tokens
          if (returnUrl) {
            const url = new URL(returnUrl);
            url.searchParams.append("token", res.data.token);
            url.searchParams.append("role", res.data.data.role);
            url.searchParams.append("user", JSON.stringify(res.data.data));
            window.location.href = url.toString();
          } else {
            nav(appLinks.home.index);
          }
        } else throw res;
      })
      .catch((ex) => {
        message.error(ex?.response?.data?.message || "Error logging in");
        const attps = ex?.response?.data?.loginAttempts;
        attps && message.info(`${4 - attps} attempts remaining...`);
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <Container size={420} mt={10}>
      <PageTitle title="Login" />
      <div className="text-center my-4">
        <img src={riviaOS} alt="CareOS Logo" width="100" />
      </div>
      {/* <Title
        align="center"
        sx={ ( theme ) => ( {
          fontFamily: `Greycliff CF, ${ theme.fontFamily }`,
          fontWeight: 900,
        } ) }
      >
        Welcome!
      </Title> */}

      <Paper withBorder p={30} mt={10} radius="md">
        <h6 className="text-center">Login to continue</h6>
        <form onSubmit={handleLogin} method="POST">
          <TextInput
            size="md"
            autoFocus
            onChange={(e) => setLogin({ ...login, email: e.target.value })}
            value={login.email}
            label="Email"
            placeholder="email here"
            required
          />
          <PasswordInput
            size="md"
            onChange={(e) => setLogin({ ...login, password: e.target.value })}
            value={login.password}
            label="Password"
            placeholder="Your password"
            required
            mt="md"
          />

          <button
            onClick={handleLogin}
            className={`button mt-3 h6 text-white app-btn btn-prim py-3 px-2 w-100  ${busy && "is-loading"
              }`}>
            Login
          </button>
          <Checkbox
            value={login.tnc}
            onChange={() => setLogin({ ...login, tnc: !login.tnc })}
            className="mt-3"
            label={
              <>
                I Accept the
                <a className="pt-2" onClick={() => setOpenModal(true)}>
                  {" "}
                  terms and conditions
                </a>
              </>
            }
          />
        </form>
      </Paper>
      <div style={{ margin: "24px 0" }}>
        <div style={{ textAlign: "center", fontWeight: 600, marginBottom: 12 }}>
          Other Actions
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "stretch",
            border: "1px solid #e0e0e0",
            borderRadius: 16,
            padding: 12,
            background: "#fafbfc",
            maxWidth: 420,
            margin: "0 auto",
          }}>
          {/* Forgot Password */}
          <div
            style={{
              flex: 1,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <Anchor
              href={appLinks.forgotPassword}
              size="sm"
              style={{ textDecoration: "none", color: "#333" }}>
              <div style={{ fontSize: 24, marginBottom: 4 }}>🔑</div>
              <div>Reset Password</div>
            </Anchor>
          </div>
          {/* Vertical Bar */}
          <div style={{ width: 1, background: "#e0e0e0", margin: "0 12px" }} />
          {/* Pharma/Lab Login */}
          <div
            style={{
              flex: 1,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <Anchor
              href={appLinks.exterals.login}
              size="sm"
              style={{ textDecoration: "none", color: "#333" }}>
              <div style={{ fontSize: 24, marginBottom: 4 }}>🧪</div>
              <div>Pharma/Lab</div>
            </Anchor>
          </div>
          {/* Vertical Bar */}
          <div style={{ width: 1, background: "#e0e0e0", margin: "0 12px" }} />
          {/* Kiosk */}
          <div
            style={{
              flex: 1,
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <Anchor
              href={appLinks.kiosk}
              size="sm"
              rel="noopener noreferrer"
              style={{ textDecoration: "none", color: "#333" }}>
              <div style={{ fontSize: 24, marginBottom: 4 }}>🖥️</div>
              <div>Kiosk</div>
            </Anchor>
          </div>
        </div>
      </div>
      {/* <div className="text-center"> */}
      <div className="text-center mt-2">
        CareOS <AppVersion className="d-inline" showTitle={false} />
      </div>
      <Modal
        title={<strong>Rivia Terms and Conditions</strong>}
        opened={modelOpen}
        onClose={() => setOpenModal(false)}>
        <p>{termsandConditions}</p>
      </Modal>
    </Container>
  );
}
